import dayjsTZ from "../../../../utils/dayjs";

/** Extract plain email from `Name <a@b.com>` or bare address. */
export function normalizeEmail(raw = "") {
  const s = String(raw || "").trim().toLowerCase();
  const m = s.match(/<([^>]+)>/);
  return (m ? m[1] : s).trim();
}

export function employeeEmail(emp) {
  if (!emp) return "";
  return normalizeEmail(
    emp.employeeEmail || emp.email || emp.workEmail || emp.userName || ""
  );
}

/**
 * Existing leave requests that match inbox email context.
 * Filters by employee (email / id) and overlapping leave dates.
 */
export function filterRequestsForEmail(employeeRequests, employees, filters = {}) {
  const emailQ = normalizeEmail(filters.email || "");
  const startKey = filters.startDate || null;
  const endKey = filters.endDate || null;
  const employeeId = filters.employeeId || null;

  const empBySub = new Map();
  const empById = new Map();
  (employees || []).forEach((e) => {
    if (e.userSub) empBySub.set(e.userSub, e);
    if (e.employeeId) empById.set(e.employeeId, e);
  });

  return (employeeRequests || [])
    .filter((r) => {
      if (!r?.requestPeriod?.length) return false;
      const emp = empBySub.get(r.userSub) || empById.get(r.employeeId);
      if (employeeId) {
        if (r.employeeId !== employeeId && emp?.employeeId !== employeeId) {
          return false;
        }
      }
      if (emailQ) {
        const fromEmp = employeeEmail(emp);
        const fromReq = normalizeEmail(
          `${r.given_name || ""} ${r.family_name || ""}`
        );
        const hay = `${fromEmp} ${r.given_name || ""} ${r.family_name || ""} ${
          emp?.employeeFirstName || ""
        } ${emp?.employeeLastName || ""}`.toLowerCase();
        if (!hay.includes(emailQ) && fromEmp !== emailQ && !fromReq.includes(emailQ)) {
          // also allow matching just the local-part
          const local = emailQ.split("@")[0];
          if (!local || !hay.includes(local)) return false;
        }
      }
      if (startKey || endKey) {
        const rStart = dayjsTZ(r.requestPeriod[0]).format("YYYY-MM-DD");
        const rEnd = dayjsTZ(r.requestPeriod.at(-1)).format("YYYY-MM-DD");
        const fStart = startKey || "1970-01-01";
        const fEnd = endKey || "2999-12-31";
        // overlap
        if (rEnd < fStart || rStart > fEnd) return false;
      }
      return true;
    })
    .sort((a, b) => (b.requestPeriod?.[0] || 0) - (a.requestPeriod?.[0] || 0))
    .slice(0, 40);
}

/** Score how closely a leave request matches AI extraction (exact preferred). */
export function matchScore(request, ai = {}) {
  if (!request?.requestPeriod?.length) return 0;
  let score = 0;
  const rStart = dayjsTZ(request.requestPeriod[0]).format("YYYY-MM-DD");
  const rEnd = dayjsTZ(request.requestPeriod.at(-1)).format("YYYY-MM-DD");
  if (ai.startDate && rStart === ai.startDate) score += 40;
  if (ai.endDate && rEnd === ai.endDate) score += 40;
  if (
    ai.startDate &&
    ai.endDate &&
    rStart === ai.startDate &&
    rEnd === ai.endDate
  ) {
    score += 10;
  }
  if (
    ai.requestType &&
    request.requestType &&
    ai.requestType === request.requestType
  ) {
    score += 10;
  }
  if (
    ai.employeeMatch?.employeeId &&
    request.employeeId === ai.employeeMatch.employeeId
  ) {
    score += 20;
  }
  if (
    ai.employeeMatch?.userSub &&
    request.userSub === ai.employeeMatch.userSub
  ) {
    score += 20;
  }
  return score;
}

export function gmailOpenUrl(threadId, gmailMessageId) {
  if (threadId) {
    return `https://mail.google.com/mail/u/0/#inbox/${threadId}`;
  }
  if (gmailMessageId) {
    return `https://mail.google.com/mail/u/0/#all/${gmailMessageId}`;
  }
  return null;
}

const BLOCKING = new Set(["Pending", "Aprovuar", "Shtyre"]);

/** Local exact duplicate among loaded leave requests (same employee + dates). */
export function findExactLeaveDuplicate(
  employeeRequests,
  { employeeId, userSub, startDate, endDate, requestType } = {}
) {
  if (!startDate || !endDate) return null;
  const type = requestType || "Pushime";
  return (
    (employeeRequests || []).find((r) => {
      if (!BLOCKING.has(r?.requestStatus)) return false;
      if (!r?.requestPeriod?.length) return false;
      if (employeeId && r.employeeId !== employeeId) return false;
      if (!employeeId && userSub && r.userSub !== userSub) return false;
      if ((r.requestType || "Pushime") !== type) return false;
      const rStart = dayjsTZ(r.requestPeriod[0]).format("YYYY-MM-DD");
      const rEnd = dayjsTZ(r.requestPeriod.at(-1)).format("YYYY-MM-DD");
      return rStart === startDate && rEnd === endDate;
    }) || null
  );
}

export function filterQueueRows(rows, { email, dateRange, status } = {}) {
  const emailQ = normalizeEmail(email || "");
  const [from, to] = dateRange || [];
  return (rows || []).filter((r) => {
    if (status && status !== "all" && r.status !== status) return false;
    if (r.status === "Dismissed") return false;
    if (emailQ) {
      const hay = `${normalizeEmail(r.fromEmail)} ${r.subject || ""} ${
        r.ai?.employeeMatch?.name || ""
      }`.toLowerCase();
      if (!hay.includes(emailQ) && !hay.includes(emailQ.split("@")[0] || "")) {
        return false;
      }
    }
    if (from && to && r.receivedAt) {
      const t = dayjsTZ(r.receivedAt);
      if (t.isBefore(from.startOf("day")) || t.isAfter(to.endOf("day"))) {
        return false;
      }
    }
    return true;
  });
}
